import _ from 'underscore';
import { useCallback, useMemo, useState } from 'react';

import {
  createSpec, specTypes, FetchPolicy,
  useMutation,
  usePagination,
  useDataRefId,
  useRefetch,
} from '@bingads-webui-react/overreact';

import {
  fetchRequestContract,
  fetchResponseContract,
} from '../spec/coll-fetch';
import {
  mutationRequestContract,
  mutationResponseContract,
} from '../spec/mutation';
import { environmentLookupFn } from '../../../environment/index';

const fetchCollSpec =
  createSpec(
    fetchRequestContract, fetchResponseContract,
    specTypes.PAGINATION, null, environmentLookupFn
  );
const refetchCollSpec =
  createSpec(
    fetchRequestContract, fetchResponseContract,
    specTypes.REFETCH, null, environmentLookupFn
  );
const mutationCollSpec =
  createSpec(
    mutationRequestContract, mutationResponseContract,
    specTypes.MUTATION, null, environmentLookupFn
  );

export function useCampaigns(dataLocator, config) {
  const dataRefId = useDataRefId(null, environmentLookupFn);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const defaultConfig = useMemo(() => ({
    mutation: config && config.mutation ? config.mutation : {},
    pagination: config && config.pagination ? config.pagination : {},
    refetch: config && config.refetch ? config.refetch : {
      onComplete: () => { setIsRefreshing(false); },
      onError: () => { setIsRefreshing(false); },
    },
  }), [config]);

  const { mutation, pagination, refetch } = defaultConfig;

  const paginationConfig = useMemo(() => ({
    fetchVariables: {
      ...dataLocator,
    },
    strictMode: true,
    ...pagination,
  }), [dataLocator, pagination]);
  const page = usePagination(dataRefId, fetchCollSpec, paginationConfig);

  const updateFn = useMutation(dataRefId, mutationCollSpec, mutation.update);

  const update = useCallback((data) => {
    updateFn({ ...dataLocator }, data);
  }, [dataLocator, updateFn]);

  const refetchFn = useRefetch(dataRefId, refetchCollSpec, refetch);

  const refresh = useCallback(() => {
    setIsRefreshing(true);

    refetchFn({
      variables: {
        ...dataLocator,
        cursorIndex: 0,
        // fetch-policy-middleware will check this fetchPolicy
        options: {
          fetchPolicy: FetchPolicy.NetworkOnly,
        },
      },
    });
  }, [dataLocator, refetchFn]);

  const [data, { isLoading, ...method }] = page;

  return [data, _.extend({}, method, {
    update,
    refresh,
    isLoading: isLoading || isRefreshing,
  })];
}
